import Description from '@/components/prayer-request/description.vue';
<template>
  <div class="mt-5 bg-white px-10 py-6 rounded-md md:mt-0 md:col-span-2">
    <form @submit.prevent>
      <h2 class="text-xl md:text-3xl mb-5 font-bold">🙏🏽 New Prayer Request</h2>

      <!-- Inputs -->
      <b-field label="Name">
        <b-input
          type="text"
          placeholder="e.g. John Doe"
          aria-placeholder="e.g. John Doe"
          v-model="name"
          required
        />
      </b-field>
      <b-field label="Email Address">
        <b-input
          required
          type="email"
          placeholder="e.g. email@example.com"
          aria-placeholder="e.g. email@example.com"
          v-model="email"
        />
      </b-field>

      <!-- Prayer request category selection markup -->
      <label class="label">
        Category
      </label>
      <div class="flex mb-5">
        <b-dropdown v-model="categoryId" :disabled="isCreatingCategory" class="mr-4" aria-role="list">
          <b-button :disabled="isCreatingCategory" :aria-disabled="isCreatingCategory" type="is-primary" outlined slot="trigger" slot-scope="{ active }" :icon-left="active ? 'caret-up' : 'caret-down'">
            {{ selectedCategory }}
          </b-button>
          <b-dropdown-item v-for="(category, index) in categories" :value="category.id" :key="index" aria-role="listitem">
            <span class="category-name my-0">{{ category.name }}</span>
            <span :class="{ 'selected-category-description': category.id === categoryId }" class="text-xs my-0 text-gray-500">{{ category.description }}</span>
          </b-dropdown-item>
        </b-dropdown>
        <template v-if="user && user.isAdmin">
          <b-button @click="isCreatingCategory = true" icon-left="plus" type="is-light" class="flex mt-4 sm:ml-auto sm:mt-0 items-center">
            Create new category
          </b-button>
        </template>
      </div>

      <template v-if="isCreatingCategory">
        <h4 class="text-lg font-bold my-3">Create new category</h4>
        <!-- Inputs -->
        <b-field label="Category name">
          <b-input
            placeholder="Category name (e.g. Healing, Finances or Thanksgiving)"
            aria-placeholder="Category name (e.g. Healing, Finances or Thanksgiving)"
            v-model="categoryName"
          />
        </b-field>

        <b-field class="mt-5" label="Category description">
          <b-input
            placeholder="Category description"
            aria-placeholder="Category description (Keep it brief)"
            v-model="categoryDescription"
          />
        </b-field>

        <div class="flex justify-end">
          <b-button
            @click="isCreatingCategory = false"
            type="is-light"
            class="mr-2"
          >
            Cancel
          </b-button>
          <b-button
            @click="createCategory"
            type="is-primary"
          >
            Create category
          </b-button>
        </div>
      </template>
      <template v-else>

        <b-field class="mt-5" label="Prayer request">
          <b-input
            placeholder="Enter your prayer request"
            aria-placeholder="Prayer request description"
            type="textarea"
            v-model="description"
          />
        </b-field>

        <div class="text-xs text-orange-600 mb-4">
          By clicking submit, you consent to Phaneroo Ministries International holding and using your information for follow up and communication purposes. Note that your personal information will not be shared publicly.
        </div>

        <div class="flex justify-end">
          <b-button
            @click="$emit('close')"
            type="is-light"
            class="mr-2"
          >
            Cancel
          </b-button>
          <b-button
            @click="createPrayerRequest"
            type="is-primary"
          >
            Submit
          </b-button>
        </div>
      </template>
      <b-loading :active.sync="isLoading" :is-full-page="false" />
    </form>
  </div>
</template>

<script>
import { useId } from '../../utils'
export default {
  name: 'PrayerRequestForm',
  inject: ['$user'],
  data () {
    return {
      name: '',
      email: '',
      title: undefined,
      description: '',
      categoryId: null,
      isCreatingCategory: false,
      categoryName: '',
      categoryDescription: '',
      isLoading: false,
      isConsentingToPrivacyPolicy: true
    }
  },
  computed: {
    user () {
      return this.$user()
    },
    categories () {
      return this.$store.getters['categories/categories']
    },
    selectedCategory () {
      if (!this.categoryId) return 'Select category'
      return this.categories[this.categoryId]['name']
    }
  },
  methods: {
    /**
     * Creates a new prayer request
     */
    async createPrayerRequest () {
      this.isLoading = true

      try {
        await this.$store.dispatch('prayerRequests/createPrayerRequest', {
          title: `Prayer_Request_Title_${useId(4)}`,
          content: this.description,
          category_id: this.categoryId,
          email: this.email,
          name: this.name
        })

        this.$emit('prayerrequest:created')
        this.$emit('close')
      } catch (error) {
        this.$logger.error('Error creating prayer request', error)
        this.$notify('There was a problem submitting your prayer request. ' + error.message, {
          type: 'error',
          title: 'Error creating prayer request',
          duration: 5000
        })
      } finally {
        this.isLoading = false
      }
    },
    /**
     * Creates a new prayer request category
     */
    async createCategory () {
      this.isLoading = true

      const payload = {
        name: this.categoryName,
        description: this.categoryDescription
      }

      try {
        await this.$store.dispatch('categories/createCategory', payload)

        this.$notify(`Category successfully created`, {
          type: 'success',
          title: `${this.categoryName} category created`
        })

        this.isCreatingCategory = false
      } catch (error) {
        this.$logger.error('Error creating category', error)
        this.$notify('There was a problem creating new category. ' + error.message, {
          type: 'error',
          title: 'Error creating category',
          duration: 5000
        })
        this.$sentry.captureException(error)
      } finally {
        this.isLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.category-name {
  @apply block;
}

.selected-category-description {
  @apply text-white;
}
</style>
